/**
 * Messaging Store
 *
 * Manages direct messaging state including conversations, messages,
 * typing indicators, and real-time WebSocket updates.
 *
 * Follows the pattern established by connection-store.ts
 */

import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import { messagesApi } from "@/services/api/messages";
import { socketClient } from "@/services/websocket/socketClient";
import { useAuthStore } from "@/stores/auth/auth-store";
import { toast } from "sonner";
import { MESSAGE_EVENTS } from "@/constants/messagingEvents";
import { getBrowserNotificationPermission } from "@/utils/browserNotifications";
import { getErrorMessageOrFallback, getErrorStatus } from "@/utils/errors";
import { logger } from "@/utils/logger";
import {
  isFailedPendingMessageError,
  markFailedPendingMessageError,
} from "./failed-pending-message-error";
import type {
  Conversation,
  ConversationParticipant,
  Message,
  PendingMessage,
  NewMessagePayload,
  MessageReadPayload,
  MessageDeletedPayload,
  MessageEditedPayload,
  TypingPayload,
  GroupCreatedPayload,
  GroupUpdatedPayload,
  GroupMemberAddedPayload,
  GroupMemberRemovedPayload,
  GroupMemberLeftPayload,
  CreateGroupRequest,
  UpdateGroupRequest,
} from "@/types/messaging";

export { MESSAGE_EVENTS };

export type NewMessageNotificationSuppressionReason =
  | "own-message"
  | "conversation-visible"
  | "conversation-muted";

export type NewMessageNotificationDecision =
  | { shouldNotify: true }
  | {
      shouldNotify: false;
      reason: NewMessageNotificationSuppressionReason;
    };

export interface NewMessageNotificationDecisionInput {
  activeConversationId: string | null;
  conversationMuted: boolean;
  currentUserId?: string;
  documentHidden: boolean;
  messageConversationId: string;
  messageSenderId: string;
}

export type BrowserNotificationSuppressionReason =
  | "desktop-preference-disabled"
  | "permission-not-granted"
  | "document-visible";

export type BrowserNotificationPermission = "default" | "denied" | "granted";

export type BrowserNotificationDecision =
  | { shouldShow: true }
  | {
      shouldShow: false;
      reason: BrowserNotificationSuppressionReason;
    };

export interface BrowserNotificationDecisionInput {
  desktopNotificationsEnabled: boolean;
  documentHidden: boolean;
  permission: BrowserNotificationPermission;
}

// ============================================================================
// Store State Interface
// ============================================================================

interface MessagingState {
  // Data
  conversations: Conversation[];
  activeConversationId: string | null;
  messages: Message[];
  pendingMessages: PendingMessage[];
  totalUnreadCount: number;
  typingUsers: Map<string, Set<string>>; // conversationId -> Set of userIds

  // Composing state (iMessage-style new conversation flow)
  isComposing: boolean;
  composingRecipients: ConversationParticipant[];

  // Counts
  conversationCount: number;

  // Loading states
  isLoadingConversations: boolean;
  isLoadingMessages: boolean;
  loadingMessagesConversationId: string | null;
  isSendingMessage: boolean;
  isLoadingMore: boolean;
  loadingMoreConversationId: string | null;
  isSettingUpWebSocketListeners: boolean;
  hasMoreMessages: boolean;

  // Error state
  error: string | null;
  websocketListenerError: string | null;

  // Cache
  lastConversationsFetch: number | null;
  lastMessagesFetch: Map<string, number>; // conversationId -> timestamp

  // Actions
  fetchConversations: (force?: boolean) => Promise<void>;
  fetchConversation: (conversationId: string) => Promise<Conversation | null>;
  startConversation: (
    recipientId: string,
    message: string,
  ) => Promise<Conversation | null>;
  setActiveConversation: (conversationId: string | null) => Promise<void>;
  loadMessages: (conversationId: string, force?: boolean) => Promise<void>;
  loadMoreMessages: () => Promise<void>;
  sendMessage: (content: string, replyToId?: string) => Promise<Message>;
  retryPendingMessage: (tempId: string) => Promise<Message | null>;
  removePendingMessage: (tempId: string) => void;
  editMessage: (messageId: string, content: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  markAsRead: (conversationId: string) => Promise<void>;
  archiveConversation: (conversationId: string) => Promise<void>;
  unarchiveConversation: (conversationId: string) => Promise<void>;
  muteConversation: (conversationId: string, mutedUntil: Date) => Promise<void>;
  unmuteConversation: (conversationId: string) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;

  // Typing indicators
  emitTypingStart: (conversationId: string) => void;
  emitTypingStop: (conversationId: string) => void;

  // Group actions
  createGroup: (
    name: string,
    memberIds: string[],
    avatar?: string,
  ) => Promise<Conversation | null>;
  updateGroup: (
    conversationId: string,
    updates: UpdateGroupRequest,
  ) => Promise<void>;
  addMembersToGroup: (
    conversationId: string,
    memberIds: string[],
  ) => Promise<void>;
  removeMemberFromGroup: (
    conversationId: string,
    memberId: string,
  ) => Promise<void>;
  leaveGroup: (conversationId: string) => Promise<void>;

  // Composing actions (iMessage-style flow)
  startComposing: () => void;
  cancelComposing: () => void;
  addComposingRecipient: (recipient: ConversationParticipant) => void;
  removeComposingRecipient: (userId: string) => void;
  clearComposingRecipients: () => void;
  sendComposingMessage: (content: string) => Promise<Conversation | null>;
  findExistingConversation: (recipientIds: string[]) => Conversation | null;
  convertToGroup: (
    conversationId: string,
    newMemberIds: string[],
    groupName?: string,
  ) => Promise<Conversation | null>;

  // Utility
  clearError: () => void;
  reset: () => void;

  // Internal WebSocket handlers
  _handleNewMessage: (payload: NewMessagePayload) => void;
  _handleMessageRead: (payload: MessageReadPayload) => void;
  _handleMessageDeleted: (payload: MessageDeletedPayload) => void;
  _handleMessageEdited: (payload: MessageEditedPayload) => void;
  _handleTypingStart: (payload: TypingPayload) => void;
  _handleTypingStop: (payload: TypingPayload) => void;
  _handleGroupCreated: (payload: GroupCreatedPayload) => void;
  _handleGroupUpdated: (payload: GroupUpdatedPayload) => void;
  _handleGroupMemberAdded: (payload: GroupMemberAddedPayload) => void;
  _handleGroupMemberRemoved: (payload: GroupMemberRemovedPayload) => void;
  _handleGroupMemberLeft: (payload: GroupMemberLeftPayload) => void;
}

// ============================================================================
// Constants
// ============================================================================

// Cache duration: 2 minutes for conversations
const CACHE_DURATION = 2 * 60 * 1000;

// Typing indicator auto-clear timeout: 3 seconds
const TYPING_TIMEOUT = 3000;

// Debounce delay for typing indicators: 300ms
const TYPING_DEBOUNCE = 300;

const logBackgroundMessagingError = (
  operation: string,
  error: unknown,
): void => {
  logger.warn("Messaging background action failed", {
    operation,
    error: getErrorMessageOrFallback(error, "Unexpected messaging error"),
  });
};

const mergeConversationPreservingRecipientState = (
  existing: Conversation,
  incoming: Conversation,
): Conversation => ({
  ...existing,
  ...incoming,
  unreadCount: existing.unreadCount,
  mutedUntil: existing.mutedUntil,
  archivedAt: existing.archivedAt,
  participant: incoming.isGroup ? undefined : incoming.participant,
});

const normalizeRecipientIdSet = (
  ids: string[],
  currentUserId?: string,
): Set<string> => {
  const normalized = new Set<string>();

  for (const id of ids) {
    if (id && id !== currentUserId) {
      normalized.add(id);
    }
  }

  return normalized;
};

const areIdSetsEqual = (a: Set<string>, b: Set<string>): boolean => {
  if (a.size !== b.size) return false;
  return Array.from(a).every((id) => b.has(id));
};

type MessagingConversationNavigator = (path: string) => void;

let messagingConversationNavigator: MessagingConversationNavigator | null =
  null;

export const getMessagingConversationPath = (conversationId: string): string =>
  `/dashboard/messages?conversation=${encodeURIComponent(conversationId)}`;

export const setMessagingConversationNavigator = (
  navigator: MessagingConversationNavigator | null,
): void => {
  messagingConversationNavigator = navigator;
};

const updateConversationPreviewFromMessage = (
  state: MessagingState,
  conversationId: string,
  sentMessage: Message,
): void => {
  const convIndex = state.conversations.findIndex(
    (conversation) => conversation.id === conversationId,
  );

  if (convIndex < 0) {
    return;
  }

  state.conversations[convIndex].lastMessage = {
    id: sentMessage.id,
    content: sentMessage.content,
    senderId: sentMessage.sender.id,
    createdAt: sentMessage.createdAt,
  };
  state.conversations[convIndex].lastMessageAt = sentMessage.createdAt;

  const conversation = state.conversations.splice(convIndex, 1)[0];
  state.conversations.unshift(conversation);
  state.lastConversationsFetch = null;
};

const updateConversationPreviewAfterMessageEdit = (
  state: MessagingState,
  editedMessage: Message,
): void => {
  const conversation = state.conversations.find(
    (item) => item.id === editedMessage.conversationId,
  );

  if (conversation?.lastMessage?.id === editedMessage.id) {
    conversation.lastMessage.content = editedMessage.content;
    state.lastConversationsFetch = null;
  }
};

const updateConversationPreviewAfterMessageDelete = (
  state: MessagingState,
  deletedMessage: Message,
): void => {
  const conversation = state.conversations.find(
    (item) => item.id === deletedMessage.conversationId,
  );

  if (!conversation || conversation.lastMessage?.id !== deletedMessage.id) {
    return;
  }

  const latestVisibleMessage = state.messages.find(
    (message) =>
      message.conversationId === deletedMessage.conversationId &&
      message.id !== deletedMessage.id &&
      !message.isDeleted,
  );

  if (latestVisibleMessage) {
    conversation.lastMessage = {
      id: latestVisibleMessage.id,
      content: latestVisibleMessage.content,
      senderId: latestVisibleMessage.sender.id,
      createdAt: latestVisibleMessage.createdAt,
    };
    conversation.lastMessageAt = latestVisibleMessage.createdAt;
  } else {
    conversation.lastMessage = undefined;
    conversation.lastMessageAt = undefined;
  }

  state.lastConversationsFetch = null;
};

const getRealtimeConversationPreview = (
  conversation: Conversation,
  message: Message,
): Conversation["lastMessage"] => {
  if (!conversation.lastMessage) {
    return {
      id: message.id,
      content: message.content,
      senderId: message.sender.id,
      createdAt: message.createdAt,
    };
  }

  return {
    ...conversation.lastMessage,
    senderId: conversation.lastMessage.senderId ?? message.sender.id,
  };
};

const hasSendingPendingMessages = (
  pendingMessages: PendingMessage[],
): boolean =>
  pendingMessages.some((pendingMessage) => pendingMessage.status === "sending");

const getMessagingError = (error: unknown, fallbackMessage: string): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  return new Error(fallbackMessage);
};

const isConversationAccessGoneError = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  return status === 401 || status === 403 || status === 404 || status === 410;
};

const CONVERSATION_UNAVAILABLE_ERROR_TAG =
  "__terraMatrixConversationUnavailable";

export class ConversationUnavailableError extends Error {
  readonly conversationId: string;
  readonly __terraMatrixConversationUnavailable = true;

  constructor(conversationId: string) {
    super(`Conversation ${conversationId} is no longer available`);
    this.name = "ConversationUnavailableError";
    this.conversationId = conversationId;
    Object.setPrototypeOf(this, ConversationUnavailableError.prototype);
  }
}

export const isConversationUnavailableError = (
  error: unknown,
): error is ConversationUnavailableError =>
  error instanceof Error &&
  Reflect.get(error, CONVERSATION_UNAVAILABLE_ERROR_TAG) === true;

const createConversationUnavailableError = (
  conversationId: string,
): ConversationUnavailableError =>
  new ConversationUnavailableError(conversationId);

const isConversationUnavailableFailure = (error: unknown): boolean =>
  isConversationUnavailableError(error) || isConversationAccessGoneError(error);

const getConversationUnavailableError = (
  error: unknown,
  conversationId: string,
): ConversationUnavailableError =>
  isConversationUnavailableError(error)
    ? error
    : createConversationUnavailableError(conversationId);

const clearConversationAccessState = (
  state: MessagingState,
  conversationId: string,
): void => {
  state.conversations = state.conversations.filter(
    (conversation) => conversation.id !== conversationId,
  );
  state.conversationCount = state.conversations.length;
  state.totalUnreadCount = state.conversations.reduce(
    (total, item) => total + item.unreadCount,
    0,
  );

  if (state.activeConversationId === conversationId) {
    state.activeConversationId = null;
    state.messages = [];
    state.hasMoreMessages = false;
  }

  state.pendingMessages = state.pendingMessages.filter(
    (pendingMessage) => pendingMessage.conversationId !== conversationId,
  );
  state.typingUsers.delete(conversationId);
  state.lastMessagesFetch.delete(conversationId);
  state.lastConversationsFetch = null;

  if (state.loadingMessagesConversationId === conversationId) {
    state.isLoadingMessages = false;
    state.loadingMessagesConversationId = null;
  }

  if (state.loadingMoreConversationId === conversationId) {
    state.isLoadingMore = false;
    state.loadingMoreConversationId = null;
  }
};

const clearActiveSelectionState = (state: MessagingState): void => {
  state.activeConversationId = null;
  state.messages = [];
  state.hasMoreMessages = false;
  state.isLoadingMore = false;
  state.loadingMoreConversationId = null;
  state.isLoadingMessages = false;
  state.loadingMessagesConversationId = null;
};

const insertMessageIfMissing = (
  state: MessagingState,
  sentMessage: Message,
): void => {
  if (!state.messages.some((message) => message.id === sentMessage.id)) {
    state.messages.unshift(sentMessage);
  }
};

const isDocumentHidden = (): boolean =>
  typeof document !== "undefined" && document.hidden;

export const isConversationVisibleForMessage = (
  activeConversationId: string | null,
  messageConversationId: string,
  documentHidden: boolean,
): boolean => activeConversationId === messageConversationId && !documentHidden;

export const isConversationMuted = (
  conversation?: Pick<Conversation, "mutedUntil">,
  now: Date = new Date(),
): boolean => {
  if (!conversation?.mutedUntil) {
    return false;
  }

  return new Date(conversation.mutedUntil) > now;
};

export const getNewMessageNotificationDecision = ({
  activeConversationId,
  conversationMuted,
  currentUserId,
  documentHidden,
  messageConversationId,
  messageSenderId,
}: NewMessageNotificationDecisionInput): NewMessageNotificationDecision => {
  if (currentUserId && messageSenderId === currentUserId) {
    return { shouldNotify: false, reason: "own-message" };
  }

  if (
    isConversationVisibleForMessage(
      activeConversationId,
      messageConversationId,
      documentHidden,
    )
  ) {
    return { shouldNotify: false, reason: "conversation-visible" };
  }

  if (conversationMuted) {
    return { shouldNotify: false, reason: "conversation-muted" };
  }

  return { shouldNotify: true };
};

export const getBrowserNotificationDecision = ({
  desktopNotificationsEnabled,
  documentHidden,
  permission,
}: BrowserNotificationDecisionInput): BrowserNotificationDecision => {
  if (!desktopNotificationsEnabled) {
    return { shouldShow: false, reason: "desktop-preference-disabled" };
  }

  if (permission !== "granted") {
    return { shouldShow: false, reason: "permission-not-granted" };
  }

  if (!documentHidden) {
    return { shouldShow: false, reason: "document-visible" };
  }

  return { shouldShow: true };
};

const openMessagingConversationFromNotification = (
  conversationId: string,
  operation: string,
): void => {
  const path = getMessagingConversationPath(conversationId);

  if (messagingConversationNavigator) {
    messagingConversationNavigator(path);
  } else if (typeof window !== "undefined") {
    window.location.assign(path);
  }

  void useMessagingStore
    .getState()
    .setActiveConversation(conversationId)
    .catch((error) => {
      logBackgroundMessagingError(operation, error);
    });
};

// ============================================================================
// Browser Notification Helper
// ============================================================================

/**
 * Show a browser push notification for a new message
 */
const getNotificationConstructor = (): typeof Notification | null => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return null;
  }

  return window.Notification;
};

const getNotificationBody = (content: string): string =>
  content.substring(0, 100) + (content.length > 100 ? "..." : "");

const showBrowserNotification = (
  senderName: string,
  content: string,
  conversationId: string,
): void => {
  const desktopNotificationsEnabled =
    useAuthStore.getState().user?.preferences?.notifications?.desktop === true;
  const decision = getBrowserNotificationDecision({
    desktopNotificationsEnabled,
    documentHidden: isDocumentHidden(),
    permission: getBrowserNotificationPermission(),
  });

  if (!decision.shouldShow) {
    return;
  }

  const NotificationConstructor = getNotificationConstructor();
  if (!NotificationConstructor) {
    return;
  }

  try {
    const notification = new NotificationConstructor(
      `New message from ${senderName}`,
      {
        body: getNotificationBody(content),
        icon: "/favicon.ico",
        tag: `message-${conversationId}`,
        requireInteraction: false,
        silent: false,
      },
    );

    // Focus window and navigate to conversation on click
    notification.onclick = () => {
      window.focus();
      openMessagingConversationFromNotification(
        conversationId,
        "notification_select_conversation",
      );
      notification.close();
    };

    // Auto-close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);
  } catch (error) {
    logBackgroundMessagingError("show_browser_notification", error);
  }
};

// ============================================================================
// Typing Indicator Management
// ============================================================================

// Track typing timeouts per user per conversation
const typingTimeouts = new Map<string, NodeJS.Timeout>();

// Track last typing emit time for debouncing
let lastTypingEmit = 0;
let typingDebounceTimer: NodeJS.Timeout | null = null;

// ============================================================================
// Store Implementation
// ============================================================================

export const useMessagingStore = create<MessagingState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state
      conversations: [],
      activeConversationId: null,
      messages: [],
      pendingMessages: [],
      totalUnreadCount: 0,
      typingUsers: new Map(),

      // Composing state
      isComposing: false,
      composingRecipients: [],

      conversationCount: 0,

      isLoadingConversations: false,
      isLoadingMessages: false,
      loadingMessagesConversationId: null,
      isSendingMessage: false,
      isLoadingMore: false,
      loadingMoreConversationId: null,
      isSettingUpWebSocketListeners: false,
      hasMoreMessages: false,

      error: null,
      websocketListenerError: null,

      lastConversationsFetch: null,
      lastMessagesFetch: new Map(),

      // ========================================================================
      // Fetch Actions
      // ========================================================================

      fetchConversations: async (force = false) => {
        const { lastConversationsFetch, isLoadingConversations } = get();

        // Skip if already loading
        if (isLoadingConversations) {
          return;
        }

        // Skip if recently fetched (within cache duration) unless forced
        if (
          !force &&
          lastConversationsFetch &&
          Date.now() - lastConversationsFetch < CACHE_DURATION
        ) {
          return;
        }

        set((state) => {
          state.isLoadingConversations = true;
          state.error = null;
        });

        try {
          const response = await messagesApi.getConversations({
            limit: 50,
            includeArchived: true,
          });

          const conversations = response.data?.conversations ?? [];
          const activeConversationId = get().activeConversationId;
          let activeConversation =
            activeConversationId &&
            !conversations.some(
              (conversation) => conversation.id === activeConversationId,
            )
              ? get().conversations.find(
                  (conversation) => conversation.id === activeConversationId,
                )
              : undefined;

          if (force && activeConversationId && activeConversation) {
            try {
              const activeResponse =
                await messagesApi.getConversation(activeConversationId);
              activeConversation =
                activeResponse.data?.id === activeConversationId
                  ? activeResponse.data
                  : activeConversation;
            } catch (error) {
              if (isConversationAccessGoneError(error)) {
                set((state) => {
                  clearConversationAccessState(state, activeConversationId);
                });
                activeConversation = undefined;
              } else {
                throw error;
              }
            }
          }

          set((state) => {
            const shouldPreserveActive =
              !!activeConversation &&
              state.activeConversationId === activeConversation.id &&
              !conversations.some(
                (conversation) => conversation.id === activeConversation.id,
              );
            const mergedConversations =
              shouldPreserveActive && activeConversation
                ? [activeConversation, ...conversations]
                : conversations;

            state.conversations = mergedConversations;
            state.conversationCount =
              response.data?.total !== undefined
                ? Math.max(response.data.total, mergedConversations.length)
                : mergedConversations.length;
            state.isLoadingConversations = false;
            state.lastConversationsFetch = Date.now();
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to fetch conversations";

          set((state) => {
            state.error = errorMessage;
            state.isLoadingConversations = false;
          });
        }
      },

      fetchConversation: async (conversationId: string) => {
        try {
          const response = await messagesApi.getConversation(conversationId);
          const conversation = response.data;

          if (conversation) {
            // Update in the conversations list if present
            set((state) => {
              const index = state.conversations.findIndex(
                (c) => c.id === conversationId,
              );
              if (index >= 0) {
                state.conversations[index] = conversation;
              } else {
                state.conversations.unshift(conversation);
              }
            });
          }

          return conversation ?? null;
        } catch (error) {
          if (isConversationAccessGoneError(error)) {
            set((state) => {
              clearConversationAccessState(state, conversationId);
            });
            return null;
          }

          throw error;
        }
      },

      startConversation: async (recipientId: string, message: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          const response = await messagesApi.startConversation(
            recipientId,
            message,
          );

          if (response.data) {
            const {
              conversation,
              message: initialMessage,
              created,
            } = response.data;

            set((state) => {
              // Invalidate cache
              state.lastConversationsFetch = null;

              // Add or update conversation in list
              const existingIndex = state.conversations.findIndex(
                (c) => c.id === conversation.id,
              );
              if (existingIndex >= 0) {
                state.conversations[existingIndex] = conversation;
              } else {
                state.conversations.unshift(conversation);
              }
              state.conversationCount = state.conversations.length;
            });

            await get().setActiveConversation(conversation.id);

            set((state) => {
              if (state.activeConversationId === conversation.id) {
                insertMessageIfMissing(state, initialMessage);
                state.lastMessagesFetch.set(conversation.id, Date.now());
              }
              updateConversationPreviewFromMessage(
                state,
                conversation.id,
                initialMessage,
              );
            });

            if (created) {
              toast.success("Conversation started");
            }

            return conversation;
          }

          return null;
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to start conversation";

          set((state) => {
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          return null;
        }
      },

      setActiveConversation: async (conversationId: string | null) => {
        const previousConversationId = get().activeConversationId;

        if (previousConversationId === conversationId) {
          if (conversationId && get().messages.length === 0) {
            await get().loadMessages(conversationId, true);
          }
          return;
        }

        // Clear typing indicator for previous conversation
        if (
          previousConversationId &&
          previousConversationId !== conversationId
        ) {
          get().emitTypingStop(previousConversationId);
        }

        set((state) => {
          clearActiveSelectionState(state);
          state.error = null;
        });

        if (!conversationId) {
          return;
        }

        const hasConversationMetadata = get().conversations.some(
          (conversation) => conversation.id === conversationId,
        );

        if (!hasConversationMetadata) {
          let hydratedConversation: Conversation | null = null;

          try {
            hydratedConversation =
              await get().fetchConversation(conversationId);
          } catch (error) {
            if (isConversationUnavailableFailure(error)) {
              set((state) => {
                clearConversationAccessState(state, conversationId);
              });
              throw getConversationUnavailableError(error, conversationId);
            }

            set((state) => {
              state.error =
                error instanceof Error
                  ? error.message
                  : "Failed to fetch conversation";
            });
            return;
          }

          if (!hydratedConversation) {
            set((state) => {
              clearConversationAccessState(state, conversationId);
            });
            throw createConversationUnavailableError(conversationId);
          }
        }

        set((state) => {
          state.isLoadingMessages = true;
          state.loadingMessagesConversationId = conversationId;
        });

        try {
          const response = await messagesApi.getMessages(conversationId, {
            limit: 50,
          });

          const messages = response.data?.messages ?? [];
          const hasMore = response.data?.hasMore ?? false;

          set((state) => {
            if (state.loadingMessagesConversationId === conversationId) {
              state.activeConversationId = conversationId;
              state.messages = messages;
              state.hasMoreMessages = hasMore;
              state.isLoadingMessages = false;
              state.loadingMessagesConversationId = null;
              state.lastMessagesFetch.set(conversationId, Date.now());
            }
          });

          if (get().activeConversationId === conversationId) {
            await get().markAsRead(conversationId);
          }
        } catch (error) {
          if (isConversationUnavailableFailure(error)) {
            set((state) => {
              clearConversationAccessState(state, conversationId);
            });
            throw getConversationUnavailableError(error, conversationId);
          }

          const errorMessage =
            error instanceof Error ? error.message : "Failed to load messages";

          set((state) => {
            state.activeConversationId = conversationId;
            state.error = errorMessage;
            if (state.loadingMessagesConversationId === conversationId) {
              state.isLoadingMessages = false;
              state.loadingMessagesConversationId = null;
            }
          });
        }
      },

      loadMessages: async (conversationId: string, force = false) => {
        const {
          lastMessagesFetch,
          isLoadingMessages,
          loadingMessagesConversationId,
        } = get();

        if (
          isLoadingMessages &&
          loadingMessagesConversationId === conversationId
        ) {
          return;
        }

        // Check cache
        const lastFetch = lastMessagesFetch.get(conversationId);
        if (!force && lastFetch && Date.now() - lastFetch < CACHE_DURATION) {
          return;
        }

        set((state) => {
          state.isLoadingMessages = true;
          state.loadingMessagesConversationId = conversationId;
          state.error = null;
        });

        try {
          const response = await messagesApi.getMessages(conversationId, {
            limit: 50,
          });

          const messages = response.data?.messages ?? [];
          const hasMore = response.data?.hasMore ?? false;

          set((state) => {
            if (state.activeConversationId === conversationId) {
              state.messages = messages;
              state.hasMoreMessages = hasMore;
            }

            if (state.loadingMessagesConversationId === conversationId) {
              state.isLoadingMessages = false;
              state.loadingMessagesConversationId = null;
            }

            state.lastMessagesFetch.set(conversationId, Date.now());
          });
        } catch (error) {
          if (isConversationUnavailableFailure(error)) {
            set((state) => {
              clearConversationAccessState(state, conversationId);
            });
            throw getConversationUnavailableError(error, conversationId);
          }

          const errorMessage =
            error instanceof Error ? error.message : "Failed to load messages";

          set((state) => {
            if (state.activeConversationId === conversationId) {
              state.error = errorMessage;
            }

            if (state.loadingMessagesConversationId === conversationId) {
              state.isLoadingMessages = false;
              state.loadingMessagesConversationId = null;
            }
          });
        }
      },

      loadMoreMessages: async () => {
        const {
          activeConversationId,
          messages,
          isLoadingMore,
          hasMoreMessages,
        } = get();

        if (!activeConversationId || isLoadingMore || !hasMoreMessages) {
          return;
        }

        const conversationId = activeConversationId;
        const oldestMessage = messages[messages.length - 1];
        const before = oldestMessage?.createdAt;

        set((state) => {
          state.isLoadingMore = true;
          state.loadingMoreConversationId = conversationId;
        });

        try {
          const response = await messagesApi.getMessages(conversationId, {
            limit: 50,
            before,
          });

          const newMessages = response.data?.messages ?? [];
          const hasMore = response.data?.hasMore ?? false;

          set((state) => {
            if (
              state.activeConversationId === conversationId &&
              state.loadingMoreConversationId === conversationId
            ) {
              state.messages = [...state.messages, ...newMessages];
              state.hasMoreMessages = hasMore;
            }

            if (state.loadingMoreConversationId === conversationId) {
              state.isLoadingMore = false;
              state.loadingMoreConversationId = null;
            }
          });
        } catch (error) {
          if (isConversationUnavailableFailure(error)) {
            set((state) => {
              clearConversationAccessState(state, conversationId);
            });
            return;
          }

          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to load more messages";

          set((state) => {
            if (state.loadingMoreConversationId === conversationId) {
              state.isLoadingMore = false;
              state.loadingMoreConversationId = null;
            }
          });

          toast.error(errorMessage);
        }
      },

      // ========================================================================
      // Message Actions
      // ========================================================================

      sendMessage: async (content: string, replyToId?: string) => {
        const { activeConversationId } = get();

        if (!activeConversationId) {
          throw new Error("No active conversation selected");
        }

        const conversationId = activeConversationId;

        // Create temporary ID for optimistic update
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

        // Add pending message (optimistic)
        const pendingMessage: PendingMessage = {
          tempId,
          conversationId,
          content,
          replyToId,
          createdAt: new Date().toISOString(),
          status: "sending",
        };

        set((state) => {
          state.pendingMessages.push(pendingMessage);
          state.isSendingMessage = true;
          state.error = null;
        });

        // Stop typing indicator
        get().emitTypingStop(conversationId);

        try {
          const response = await messagesApi.sendMessage(
            conversationId,
            content,
            replyToId,
          );

          if (response.data?.message) {
            const sentMessage = response.data.message;

            set((state) => {
              // Remove pending message
              state.pendingMessages = state.pendingMessages.filter(
                (p) => p.tempId !== tempId,
              );

              if (state.activeConversationId === conversationId) {
                insertMessageIfMissing(state, sentMessage);
              }

              state.isSendingMessage = hasSendingPendingMessages(
                state.pendingMessages,
              );
              updateConversationPreviewFromMessage(
                state,
                conversationId,
                sentMessage,
              );
            });

            return sentMessage;
          }

          throw new Error("Failed to send message");
        } catch (error) {
          const sendError = getMessagingError(error, "Failed to send message");
          const errorMessage = sendError.message;

          // Mark pending message as failed
          set((state) => {
            const pendingIndex = state.pendingMessages.findIndex(
              (p) => p.tempId === tempId,
            );
            if (pendingIndex >= 0) {
              state.pendingMessages[pendingIndex].status = "failed";
            }
            state.isSendingMessage = hasSendingPendingMessages(
              state.pendingMessages,
            );
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          throw markFailedPendingMessageError(sendError);
        }
      },

      retryPendingMessage: async (tempId: string) => {
        const pendingMessage = get().pendingMessages.find(
          (p) => p.tempId === tempId,
        );

        if (!pendingMessage) {
          return null;
        }

        set((state) => {
          const pendingIndex = state.pendingMessages.findIndex(
            (p) => p.tempId === tempId,
          );
          if (pendingIndex >= 0) {
            state.pendingMessages[pendingIndex].status = "sending";
          }
          state.isSendingMessage = true;
          state.error = null;
        });

        try {
          const response = await messagesApi.sendMessage(
            pendingMessage.conversationId,
            pendingMessage.content,
            pendingMessage.replyToId,
          );

          if (response.data?.message) {
            const sentMessage = response.data.message;

            set((state) => {
              state.pendingMessages = state.pendingMessages.filter(
                (p) => p.tempId !== tempId,
              );

              if (
                state.activeConversationId === pendingMessage.conversationId
              ) {
                insertMessageIfMissing(state, sentMessage);
              }

              state.isSendingMessage = hasSendingPendingMessages(
                state.pendingMessages,
              );
              updateConversationPreviewFromMessage(
                state,
                pendingMessage.conversationId,
                sentMessage,
              );
            });

            return sentMessage;
          }

          set((state) => {
            const pendingIndex = state.pendingMessages.findIndex(
              (p) => p.tempId === tempId,
            );
            if (pendingIndex >= 0) {
              state.pendingMessages[pendingIndex].status = "failed";
            }
            state.isSendingMessage = hasSendingPendingMessages(
              state.pendingMessages,
            );
          });
          return null;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to send message";

          set((state) => {
            const pendingIndex = state.pendingMessages.findIndex(
              (p) => p.tempId === tempId,
            );
            if (pendingIndex >= 0) {
              state.pendingMessages[pendingIndex].status = "failed";
            }
            state.isSendingMessage = hasSendingPendingMessages(
              state.pendingMessages,
            );
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          return null;
        }
      },

      removePendingMessage: (tempId: string) => {
        set((state) => {
          state.pendingMessages = state.pendingMessages.filter(
            (p) => p.tempId !== tempId,
          );
        });
      },

      editMessage: async (messageId: string, content: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          const response = await messagesApi.editMessage(messageId, content);

          if (response.data?.message) {
            const editedMessage = response.data.message;

            set((state) => {
              const index = state.messages.findIndex((m) => m.id === messageId);
              if (index >= 0) {
                state.messages[index] = editedMessage;
              }
              updateConversationPreviewAfterMessageEdit(state, editedMessage);
            });

            toast.success("Message edited");
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to edit message";

          set((state) => {
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          throw error;
        }
      },

      deleteMessage: async (messageId: string) => {
        // Optimistic update
        const originalMessage = get().messages.find((m) => m.id === messageId);
        const originalConversation = originalMessage
          ? get().conversations.find(
              (c) => c.id === originalMessage.conversationId,
            )
          : undefined;
        const originalLastMessage = originalConversation?.lastMessage
          ? { ...originalConversation.lastMessage }
          : undefined;
        const originalLastMessageAt = originalConversation?.lastMessageAt;
        const deletedMessageWasPreview =
          originalConversation?.lastMessage?.id === messageId;

        set((state) => {
          const index = state.messages.findIndex((m) => m.id === messageId);
          if (index >= 0) {
            state.messages[index].isDeleted = true;
            state.messages[index].content = "This message was deleted";
          }
          if (originalMessage) {
            updateConversationPreviewAfterMessageDelete(state, originalMessage);
          }
          state.error = null;
        });

        try {
          await messagesApi.deleteMessage(messageId);
          toast.success("Message deleted");
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to delete message";

          // Rollback optimistic update
          if (originalMessage) {
            set((state) => {
              const index = state.messages.findIndex((m) => m.id === messageId);
              if (index >= 0) {
                state.messages[index] = originalMessage;
              }
              if (deletedMessageWasPreview) {
                const conversationIndex = state.conversations.findIndex(
                  (conversation) =>
                    conversation.id === originalMessage.conversationId,
                );
                if (conversationIndex >= 0) {
                  state.conversations[conversationIndex].lastMessage =
                    originalLastMessage;
                  state.conversations[conversationIndex].lastMessageAt =
                    originalLastMessageAt;
                  state.lastConversationsFetch = null;
                }
              }
              state.error = errorMessage;
            });
          }

          toast.error(errorMessage);
          throw error;
        }
      },

      // ========================================================================
      // Conversation Management
      // ========================================================================

      markAsRead: async (conversationId: string) => {
        try {
          await messagesApi.markAsRead(conversationId);

          set((state) => {
            // Update unread count for conversation
            const convIndex = state.conversations.findIndex(
              (c) => c.id === conversationId,
            );
            if (
              convIndex >= 0 &&
              state.conversations[convIndex].unreadCount > 0
            ) {
              const previousUnread = state.conversations[convIndex].unreadCount;
              state.conversations[convIndex].unreadCount = 0;
              state.totalUnreadCount = Math.max(
                0,
                state.totalUnreadCount - previousUnread,
              );
            }
          });
        } catch (error) {
          if (isConversationAccessGoneError(error)) {
            set((state) => {
              clearConversationAccessState(state, conversationId);
            });
            throw createConversationUnavailableError(conversationId);
          }

          const errorMessage =
            error instanceof Error ? error.message : "Failed to mark as read";

          set((state) => {
            state.error = errorMessage;
          });

          logBackgroundMessagingError("mark_as_read", error);
        }
      },

      archiveConversation: async (conversationId: string) => {
        // Optimistic update
        const originalConversation = get().conversations.find(
          (c) => c.id === conversationId,
        );

        set((state) => {
          const index = state.conversations.findIndex(
            (c) => c.id === conversationId,
          );
          if (index >= 0) {
            state.conversations[index].archivedAt = new Date().toISOString();
          }
          state.error = null;
        });

        try {
          await messagesApi.archiveConversation(conversationId);
          toast.success("Conversation archived");
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to archive conversation";

          // Rollback
          if (originalConversation) {
            set((state) => {
              const index = state.conversations.findIndex(
                (c) => c.id === conversationId,
              );
              if (index >= 0) {
                state.conversations[index].archivedAt =
                  originalConversation.archivedAt;
              }
              state.error = errorMessage;
            });
          }

          toast.error(errorMessage);
          throw error;
        }
      },

      unarchiveConversation: async (conversationId: string) => {
        const originalConversation = get().conversations.find(
          (c) => c.id === conversationId,
        );

        set((state) => {
          const index = state.conversations.findIndex(
            (c) => c.id === conversationId,
          );
          if (index >= 0) {
            state.conversations[index].archivedAt = undefined;
          }
          state.error = null;
        });

        try {
          await messagesApi.unarchiveConversation(conversationId);
          toast.success("Conversation unarchived");
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to unarchive conversation";

          if (originalConversation) {
            set((state) => {
              const index = state.conversations.findIndex(
                (c) => c.id === conversationId,
              );
              if (index >= 0) {
                state.conversations[index].archivedAt =
                  originalConversation.archivedAt;
              }
              state.error = errorMessage;
            });
          }

          toast.error(errorMessage);
          throw error;
        }
      },

      muteConversation: async (conversationId: string, mutedUntil: Date) => {
        const originalMutedUntil = get().conversations.find(
          (c) => c.id === conversationId,
        )?.mutedUntil;

        set((state) => {
          const index = state.conversations.findIndex(
            (c) => c.id === conversationId,
          );
          if (index >= 0) {
            state.conversations[index].mutedUntil = mutedUntil.toISOString();
          }
          state.error = null;
        });

        try {
          await messagesApi.muteConversation(conversationId, mutedUntil);
          toast.success("Conversation muted");
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to mute conversation";

          set((state) => {
            const index = state.conversations.findIndex(
              (c) => c.id === conversationId,
            );
            if (index >= 0) {
              state.conversations[index].mutedUntil = originalMutedUntil;
            }
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          throw error;
        }
      },

      unmuteConversation: async (conversationId: string) => {
        const originalMutedUntil = get().conversations.find(
          (c) => c.id === conversationId,
        )?.mutedUntil;

        set((state) => {
          const index = state.conversations.findIndex(
            (c) => c.id === conversationId,
          );
          if (index >= 0) {
            state.conversations[index].mutedUntil = undefined;
          }
          state.error = null;
        });

        try {
          await messagesApi.unmuteConversation(conversationId);
          toast.success("Conversation unmuted");
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to unmute conversation";

          set((state) => {
            const index = state.conversations.findIndex(
              (c) => c.id === conversationId,
            );
            if (index >= 0) {
              state.conversations[index].mutedUntil = originalMutedUntil;
            }
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          throw error;
        }
      },

      fetchUnreadCount: async () => {
        try {
          const response = await messagesApi.getUnreadCount();
          const count = response.data?.count ?? 0;

          set((state) => {
            state.totalUnreadCount = count;
          });
        } catch (error) {
          logBackgroundMessagingError("fetch_unread_count", error);
        }
      },

      // ========================================================================
      // Group Actions
      // ========================================================================

      createGroup: async (
        name: string,
        memberIds: string[],
        avatar?: string,
      ) => {
        set((state) => {
          state.error = null;
        });

        try {
          const request: CreateGroupRequest = { name, memberIds, avatar };
          const response = await messagesApi.createGroup(request);

          if (response.data?.conversation) {
            const { conversation } = response.data;

            set((state) => {
              // Invalidate cache
              state.lastConversationsFetch = null;
              // Add group to conversations list
              state.conversations.unshift(conversation);
              state.conversationCount = state.conversations.length;
            });

            toast.success("Group created");
            return conversation;
          }

          return null;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to create group";

          set((state) => {
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          return null;
        }
      },

      updateGroup: async (
        conversationId: string,
        updates: UpdateGroupRequest,
      ) => {
        // Store original for rollback
        const originalConv = get().conversations.find(
          (c) => c.id === conversationId,
        );

        // Optimistic update
        set((state) => {
          const index = state.conversations.findIndex(
            (c) => c.id === conversationId,
          );
          if (index >= 0) {
            if (updates.name) {
              state.conversations[index].groupName = updates.name;
            }
            if (updates.avatar !== undefined) {
              state.conversations[index].groupAvatar = updates.avatar;
            }
          }
          state.error = null;
        });

        try {
          await messagesApi.updateGroup(conversationId, updates);
          toast.success("Group updated");
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to update group";

          // Rollback
          if (originalConv) {
            set((state) => {
              const index = state.conversations.findIndex(
                (c) => c.id === conversationId,
              );
              if (index >= 0) {
                state.conversations[index].groupName = originalConv.groupName;
                state.conversations[index].groupAvatar =
                  originalConv.groupAvatar;
              }
              state.error = errorMessage;
            });
          }

          toast.error(errorMessage);
          throw error;
        }
      },

      addMembersToGroup: async (
        conversationId: string,
        memberIds: string[],
      ) => {
        set((state) => {
          state.error = null;
        });

        try {
          await messagesApi.addMembers(conversationId, { memberIds });

          // Refresh the conversation to get updated participants
          await get().fetchConversation(conversationId);

          toast.success(
            `Added ${memberIds.length} member${memberIds.length !== 1 ? "s" : ""}`,
          );
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to add members";

          set((state) => {
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          throw error;
        }
      },

      removeMemberFromGroup: async (
        conversationId: string,
        memberId: string,
      ) => {
        // Store original for rollback
        const originalConv = get().conversations.find(
          (c) => c.id === conversationId,
        );

        // Optimistic update
        set((state) => {
          const index = state.conversations.findIndex(
            (c) => c.id === conversationId,
          );
          if (index >= 0 && state.conversations[index].participants) {
            state.conversations[index].participants = state.conversations[
              index
            ].participants?.filter((p) => p.id !== memberId);
          }
          state.error = null;
        });

        try {
          await messagesApi.removeMember(conversationId, memberId);
          if (memberId === useAuthStore.getState().user?.id) {
            set((state) => {
              clearConversationAccessState(state, conversationId);
            });
          }
          toast.success("Member removed");
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to remove member";

          // Rollback
          if (originalConv) {
            set((state) => {
              const index = state.conversations.findIndex(
                (c) => c.id === conversationId,
              );
              if (index >= 0) {
                state.conversations[index].participants =
                  originalConv.participants;
              }
              state.error = errorMessage;
            });
          }

          toast.error(errorMessage);
          throw error;
        }
      },

      leaveGroup: async (conversationId: string) => {
        set((state) => {
          state.error = null;
        });

        try {
          await messagesApi.leaveGroup(conversationId);

          set((state) => {
            clearConversationAccessState(state, conversationId);
          });

          toast.success("Left group");
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to leave group";

          set((state) => {
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          throw error;
        }
      },

      // ========================================================================
      // Composing Actions (iMessage-style flow)
      // ========================================================================

      startComposing: () => {
        // Stop typing indicators for the previous conversation
        const { activeConversationId } = get();
        if (activeConversationId) {
          get().emitTypingStop(activeConversationId);
        }

        set((state) => {
          state.isComposing = true;
          state.composingRecipients = [];
          state.activeConversationId = null;
          state.messages = [];
        });
      },

      cancelComposing: () => {
        set((state) => {
          state.isComposing = false;
          state.composingRecipients = [];
        });
      },

      addComposingRecipient: (recipient: ConversationParticipant) => {
        set((state) => {
          // Prevent duplicates - use spread to ensure new array reference for useShallow
          if (!state.composingRecipients.some((r) => r.id === recipient.id)) {
            state.composingRecipients = [
              ...state.composingRecipients,
              recipient,
            ];
          }
        });
      },

      removeComposingRecipient: (userId: string) => {
        set((state) => {
          state.composingRecipients = state.composingRecipients.filter(
            (r) => r.id !== userId,
          );
        });
      },

      clearComposingRecipients: () => {
        set((state) => {
          state.composingRecipients = [];
        });
      },

      findExistingConversation: (recipientIds: string[]) => {
        const { conversations } = get();

        if (recipientIds.length === 0) {
          return null;
        }

        // Single recipient: find 1:1 conversation (exclude archived)
        if (recipientIds.length === 1) {
          const recipientId = recipientIds[0];
          const existing = conversations.find(
            (c) =>
              !c.isGroup && !c.archivedAt && c.participant?.id === recipientId,
          );
          return existing ?? null;
        }

        // Multiple recipients: find group with exact same intended members (exclude archived).
        // Group participant lists include the current user; compose recipient lists usually do not.
        const currentUserId = useAuthStore.getState().user?.id;
        const recipientSet = normalizeRecipientIdSet(
          recipientIds,
          currentUserId,
        );
        if (recipientSet.size === 0) {
          return null;
        }

        const existing = conversations.find((c) => {
          if (!c.isGroup || !c.participants || c.archivedAt) return false;
          const participantSet = normalizeRecipientIdSet(
            c.participants.map((p) => p.id),
            currentUserId,
          );
          return areIdSetsEqual(participantSet, recipientSet);
        });

        return existing ?? null;
      },

      sendComposingMessage: async (content: string) => {
        const { composingRecipients, isComposing } = get();

        if (!isComposing || composingRecipients.length === 0) {
          return null;
        }

        set((state) => {
          state.isSendingMessage = true;
          state.error = null;
        });

        let conversation: Conversation | null = null;

        try {
          const recipientIds = composingRecipients.map((r) => r.id);

          // Check for existing conversation first
          const existing = get().findExistingConversation(recipientIds);

          if (existing) {
            // Switch to existing conversation and send message there
            conversation = existing;
            await get().setActiveConversation(existing.id);
            await get().sendMessage(content);
          } else if (recipientIds.length === 1) {
            // Create new 1:1 conversation
            conversation = await get().startConversation(
              recipientIds[0],
              content,
            );
          } else {
            // Create new group conversation
            // Generate group name from participant names
            const names = composingRecipients
              .slice(0, 3)
              .map((r) => r.firstName || r.username)
              .join(", ");
            const groupName =
              composingRecipients.length > 3
                ? `${names} +${composingRecipients.length - 3}`
                : names;

            conversation = await get().createGroup(groupName, recipientIds);

            // Send the first message to the group
            if (conversation) {
              await get().setActiveConversation(conversation.id);
              await get().sendMessage(content);
            }
          }

          // Exit composing mode on success
          if (conversation) {
            const conversationId = conversation.id;
            set((state) => {
              state.isComposing = false;
              state.composingRecipients = [];
              state.isSendingMessage = false;
              state.activeConversationId = conversationId;
            });
          }

          set((state) => {
            state.isSendingMessage = false;
          });

          return conversation;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to send message";

          if (conversation && isFailedPendingMessageError(error)) {
            const conversationId = conversation.id;
            set((state) => {
              state.isComposing = false;
              state.composingRecipients = [];
              state.isSendingMessage = false;
              state.activeConversationId = conversationId;
              state.error = errorMessage;
            });

            return conversation;
          }

          set((state) => {
            state.error = errorMessage;
            state.isSendingMessage = false;
          });

          toast.error(errorMessage);
          return null;
        }
      },

      convertToGroup: async (
        conversationId: string,
        newMemberIds: string[],
        groupName?: string,
      ) => {
        set((state) => {
          state.error = null;
        });

        try {
          const response = await messagesApi.convertToGroup(
            conversationId,
            newMemberIds,
            groupName,
          );

          if (response.data?.conversation) {
            const updatedConversation = response.data.conversation;

            set((state) => {
              // Update the conversation in the list
              const index = state.conversations.findIndex(
                (c) => c.id === conversationId,
              );
              if (index >= 0) {
                state.conversations[index] = updatedConversation;
              }
              // Invalidate cache
              state.lastConversationsFetch = null;
            });

            toast.success("Conversation converted to group");
            return updatedConversation;
          }

          return null;
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to convert to group";

          set((state) => {
            state.error = errorMessage;
          });

          toast.error(errorMessage);
          return null;
        }
      },

      // ========================================================================
      // Typing Indicators
      // ========================================================================

      emitTypingStart: (conversationId: string) => {
        const now = Date.now();

        // Debounce typing emissions
        if (now - lastTypingEmit < TYPING_DEBOUNCE) {
          // Clear existing debounce timer
          if (typingDebounceTimer) {
            clearTimeout(typingDebounceTimer);
          }
          // Set new timer
          typingDebounceTimer = setTimeout(() => {
            get().emitTypingStart(conversationId);
          }, TYPING_DEBOUNCE);
          return;
        }

        lastTypingEmit = now;

        if (socketClient.isConnected()) {
          socketClient.emitCustomEvent(MESSAGE_EVENTS.TYPING_START, {
            conversationId,
          });
        }
      },

      emitTypingStop: (conversationId: string) => {
        // Cancel the pending debounce
        if (typingDebounceTimer) {
          clearTimeout(typingDebounceTimer);
          typingDebounceTimer = null;
        }

        if (socketClient.isConnected()) {
          socketClient.emitCustomEvent(MESSAGE_EVENTS.TYPING_STOP, {
            conversationId,
          });
        }
      },

      // ========================================================================
      // Utility Actions
      // ========================================================================

      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },

      reset: () => {
        cleanupMessagingWebSocketListeners();
        resetMessagingSocketRefreshState();

        // Clear all typing timeouts
        typingTimeouts.forEach((timeout) => clearTimeout(timeout));
        typingTimeouts.clear();

        if (typingDebounceTimer) {
          clearTimeout(typingDebounceTimer);
          typingDebounceTimer = null;
        }

        set((state) => {
          state.conversations = [];
          state.activeConversationId = null;
          state.messages = [];
          state.pendingMessages = [];
          state.totalUnreadCount = 0;
          state.typingUsers = new Map();
          state.conversationCount = 0;
          state.isLoadingConversations = false;
          state.isLoadingMessages = false;
          state.loadingMessagesConversationId = null;
          state.isSendingMessage = false;
          state.isLoadingMore = false;
          state.loadingMoreConversationId = null;
          state.isSettingUpWebSocketListeners = false;
          state.hasMoreMessages = false;
          state.error = null;
          state.websocketListenerError = null;
          state.lastConversationsFetch = null;
          state.lastMessagesFetch = new Map();
        });
      },

      // ========================================================================
      // WebSocket Event Handlers (Internal)
      // ========================================================================

      _handleNewMessage: (payload: NewMessagePayload) => {
        const { message, conversation } = payload;
        const { activeConversationId, conversations } = get();
        const documentHidden = isDocumentHidden();
        const currentUserId = useAuthStore.getState().user?.id;
        const activeConversationIsVisible = isConversationVisibleForMessage(
          activeConversationId,
          message.conversationId,
          documentHidden,
        );
        const lastMessage = getRealtimeConversationPreview(
          conversation,
          message,
        );

        // Check if conversation is muted
        const existingConv = conversations.find(
          (c) => c.id === conversation.id,
        );
        const conversationMuted = isConversationMuted(existingConv);

        set((state) => {
          // Update or add conversation
          const convIndex = state.conversations.findIndex(
            (c) => c.id === conversation.id,
          );

          if (convIndex >= 0) {
            const nextUnreadCount = activeConversationIsVisible
              ? 0
              : conversation.unreadCount;
            // Update existing conversation
            state.conversations[convIndex] = {
              ...state.conversations[convIndex],
              lastMessage,
              lastMessageAt: conversation.lastMessageAt ?? message.createdAt,
              unreadCount: nextUnreadCount,
            };
            // Move to top
            const conv = state.conversations.splice(convIndex, 1)[0];
            state.conversations.unshift(conv);
          } else {
            // Add new conversation
            state.conversations.unshift({
              ...conversation,
              lastMessage,
              lastMessageAt: conversation.lastMessageAt ?? message.createdAt,
              unreadCount: activeConversationIsVisible
                ? 0
                : conversation.unreadCount,
            });
            state.conversationCount++;
          }

          // Add message if this is the active conversation
          if (activeConversationId === message.conversationId) {
            // Check if message already exists (prevent duplicates)
            const exists = state.messages.some((m) => m.id === message.id);
            if (!exists) {
              state.messages.unshift(message);
            }
          }

          // Invalidate conversations cache
          state.lastConversationsFetch = null;
          state.lastMessagesFetch.delete(message.conversationId);
          state.totalUnreadCount = state.conversations.reduce(
            (total, item) => total + item.unreadCount,
            0,
          );
        });

        if (activeConversationIsVisible) {
          void messagesApi.markAsRead(message.conversationId).catch((error) => {
            logBackgroundMessagingError("realtime_mark_as_read", error);
          });
        }

        const notificationDecision = getNewMessageNotificationDecision({
          activeConversationId,
          conversationMuted,
          currentUserId,
          documentHidden,
          messageConversationId: message.conversationId,
          messageSenderId: message.sender.id,
        });

        if (notificationDecision.shouldNotify) {
          const senderName =
            `${message.sender.firstName} ${message.sender.lastName}`.trim() ||
            message.sender.username;

          // Show toast notification
          toast.info(`New message from ${senderName}`, {
            description:
              message.content.substring(0, 50) +
              (message.content.length > 50 ? "..." : ""),
            action: {
              label: "View",
              onClick: () => {
                openMessagingConversationFromNotification(
                  message.conversationId,
                  "toast_select_conversation",
                );
              },
            },
          });

          showBrowserNotification(
            senderName,
            message.content,
            message.conversationId,
          );
        }
      },

      _handleMessageRead: (payload: MessageReadPayload) => {
        const { conversationId, userId, readAt, unreadCount } = payload;

        set((state) => {
          // Update read receipts for messages in the active conversation
          if (state.activeConversationId === conversationId) {
            state.messages.forEach((msg) => {
              if (msg.sender.id !== userId) {
                // Add read receipt if not already present
                const alreadyRead = msg.readBy.some((r) => r.userId === userId);
                if (!alreadyRead) {
                  msg.readBy.push({ userId, readAt });
                }
              }
            });
          }

          if (unreadCount !== undefined) {
            const convIndex = state.conversations.findIndex(
              (c) => c.id === conversationId,
            );
            if (convIndex >= 0) {
              state.conversations[convIndex].unreadCount = unreadCount;
              state.totalUnreadCount = state.conversations.reduce(
                (total, item) => total + item.unreadCount,
                0,
              );
            }
          }
        });
      },

      _handleMessageDeleted: (payload: MessageDeletedPayload) => {
        const { messageId, conversationId, conversation } = payload;

        set((state) => {
          // Update message if in active conversation
          if (state.activeConversationId === conversationId) {
            const msgIndex = state.messages.findIndex(
              (m) => m.id === messageId,
            );
            if (msgIndex >= 0) {
              state.messages[msgIndex].isDeleted = true;
              state.messages[msgIndex].content = "This message was deleted";
            }
          }

          const convIndex = state.conversations.findIndex(
            (c) => c.id === conversationId,
          );
          if (
            convIndex >= 0 &&
            state.conversations[convIndex].lastMessage?.id === messageId
          ) {
            if (conversation) {
              state.conversations[convIndex] =
                mergeConversationPreservingRecipientState(
                  state.conversations[convIndex],
                  conversation,
                );
            } else {
              state.conversations[convIndex].lastMessage.content =
                "This message was deleted";
            }
          }
        });
      },

      _handleMessageEdited: (payload: MessageEditedPayload) => {
        const { message } = payload;

        set((state) => {
          // Update message if in active conversation
          if (state.activeConversationId === message.conversationId) {
            const msgIndex = state.messages.findIndex(
              (m) => m.id === message.id,
            );
            if (msgIndex >= 0) {
              state.messages[msgIndex] = message;
            }
          }

          // Update last message preview in conversation if it was the last message
          const convIndex = state.conversations.findIndex(
            (c) => c.id === message.conversationId,
          );
          if (convIndex >= 0) {
            const conv = state.conversations[convIndex];
            if (conv.lastMessage?.id === message.id) {
              conv.lastMessage.content = message.content;
            }
          }
        });
      },

      _handleTypingStart: (payload: TypingPayload) => {
        const { conversationId, userId } = payload;
        const key = `${conversationId}:${userId}`;

        // Clear existing timeout for this user
        const existingTimeout = typingTimeouts.get(key);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        set((state) => {
          const users =
            state.typingUsers.get(conversationId) ?? new Set<string>();
          users.add(userId);
          state.typingUsers.set(conversationId, users);
        });

        // Set auto-clear timeout
        const timeout = setTimeout(() => {
          set((state) => {
            const users = state.typingUsers.get(conversationId);
            if (users) {
              users.delete(userId);
              if (users.size === 0) {
                state.typingUsers.delete(conversationId);
              }
            }
          });
          typingTimeouts.delete(key);
        }, TYPING_TIMEOUT);

        typingTimeouts.set(key, timeout);
      },

      _handleTypingStop: (payload: TypingPayload) => {
        const { conversationId, userId } = payload;
        const key = `${conversationId}:${userId}`;

        // Clear timeout
        const timeout = typingTimeouts.get(key);
        if (timeout) {
          clearTimeout(timeout);
          typingTimeouts.delete(key);
        }

        set((state) => {
          const users = state.typingUsers.get(conversationId);
          if (users) {
            users.delete(userId);
            if (users.size === 0) {
              state.typingUsers.delete(conversationId);
            }
          }
        });
      },

      // ========================================================================
      // Group WebSocket Event Handlers
      // ========================================================================

      _handleGroupCreated: (payload: GroupCreatedPayload) => {
        const { conversation } = payload;
        let inserted = false;

        set((state) => {
          const index = state.conversations.findIndex(
            (c) => c.id === conversation.id,
          );
          if (index >= 0) {
            state.conversations[index] =
              mergeConversationPreservingRecipientState(
                state.conversations[index],
                conversation,
              );
          } else {
            state.conversations.unshift(conversation);
            state.conversationCount = state.conversations.length;
            inserted = true;
          }
          state.lastConversationsFetch = null;
        });

        if (inserted) {
          toast.info(`You were added to group: ${conversation.groupName}`);
        }
      },

      _handleGroupUpdated: (payload: GroupUpdatedPayload) => {
        const { conversation } = payload;

        set((state) => {
          const index = state.conversations.findIndex(
            (c) => c.id === conversation.id,
          );
          if (index >= 0) {
            state.conversations[index] =
              mergeConversationPreservingRecipientState(
                state.conversations[index],
                conversation,
              );
          }
        });
      },

      _handleGroupMemberAdded: (payload: GroupMemberAddedPayload) => {
        const { conversationId, members, conversation } = payload;

        set((state) => {
          const index = state.conversations.findIndex(
            (c) => c.id === conversationId,
          );
          if (index >= 0) {
            if (conversation) {
              state.conversations[index] =
                mergeConversationPreservingRecipientState(
                  state.conversations[index],
                  conversation,
                );
            }

            const participants = state.conversations[index].participants;
            if (!participants) {
              return;
            }

            // Add new members that don't already exist
            const existingIds = new Set(participants.map((p) => p.id));
            const newMembers = members.filter((m) => !existingIds.has(m.id));
            state.conversations[index].participants = [
              ...participants,
              ...newMembers,
            ];
          } else if (conversation) {
            state.conversations.unshift(conversation);
            state.conversationCount = state.conversations.length;
            state.lastConversationsFetch = null;
          }
        });
      },

      _handleGroupMemberRemoved: (payload: GroupMemberRemovedPayload) => {
        const { conversationId, memberId, wasRemoved } = payload;

        set((state) => {
          const index = state.conversations.findIndex(
            (c) => c.id === conversationId,
          );
          if (wasRemoved && index >= 0) {
            clearConversationAccessState(state, conversationId);
            return;
          }
          if (index >= 0 && state.conversations[index].participants) {
            const participants = state.conversations[index].participants ?? [];
            state.conversations[index].participants = participants.filter(
              (p) => p.id !== memberId,
            );
          }
        });
      },

      _handleGroupMemberLeft: (payload: GroupMemberLeftPayload) => {
        const { conversationId, memberId } = payload;

        set((state) => {
          if (memberId === useAuthStore.getState().user?.id) {
            clearConversationAccessState(state, conversationId);
            return;
          }

          const index = state.conversations.findIndex(
            (c) => c.id === conversationId,
          );
          if (index >= 0 && state.conversations[index].participants) {
            const participants = state.conversations[index].participants ?? [];
            state.conversations[index].participants = participants.filter(
              (p) => p.id !== memberId,
            );
          }
        });
      },
    })),
  ),
);

// ============================================================================
// Auth Store Integration - Reset on Logout
// ============================================================================

if (typeof window !== "undefined") {
  void import("../auth/auth-store")
    .then(({ useAuthStore }) => {
      useAuthStore.subscribe(
        (state) => state.isAuthenticated,
        (isAuthenticated, previousIsAuthenticated) => {
          // If user logged out, reset messaging state
          if (previousIsAuthenticated && !isAuthenticated) {
            useMessagingStore.getState().reset();
          }
          // If user logged in, fetch unread count
          else if (!previousIsAuthenticated && isAuthenticated) {
            setTimeout(() => {
              void useMessagingStore
                .getState()
                .fetchUnreadCount()
                .catch((error) => {
                  logBackgroundMessagingError("login_fetch_unread_count", error);
                });
            }, 500);
          }
        },
      );
    })
    .catch((error) => {
      logBackgroundMessagingError("auth_store_subscription", error);
    });
}

// ============================================================================
// WebSocket Listeners
// ============================================================================

let wsListenersRegistered = false;
let registeredMessagingCustomUnsubscribers: Array<() => void> = [];
let registeredMessagingSocketUnsubscribers: Array<() => void> = [];
const MESSAGING_SOCKET_REFRESH_DEBOUNCE_MS = 1000;
let lastMessagingSocketRefreshAt: number | null = null;
let messagingSocketRefreshInFlight: Promise<void> | null = null;

const resetMessagingSocketRefreshState = (): void => {
  lastMessagingSocketRefreshAt = null;
  messagingSocketRefreshInFlight = null;
};

export const setupMessagingWebSocketListeners = (): void => {
  if (wsListenersRegistered) {
    useMessagingStore.setState({
      isSettingUpWebSocketListeners: false,
      websocketListenerError: null,
    });
    return;
  }

  useMessagingStore.setState({
    isSettingUpWebSocketListeners: true,
    websocketListenerError: null,
  });

  if (!socketClient.isConnected()) {
    useMessagingStore.setState({
      isSettingUpWebSocketListeners: false,
      websocketListenerError: "Messaging websocket is not connected",
    });
    return;
  }

  const registerCustomEvent = <Payload>(
    event: string,
    handler: (payload: Payload) => void,
  ): void => {
    socketClient.onCustomEvent(event, handler);
    registeredMessagingCustomUnsubscribers.push(() => {
      socketClient.offCustomEvent(event, handler);
    });
  };

  try {
    // Register listener for new message
    registerCustomEvent(
      MESSAGE_EVENTS.NEW_MESSAGE,
      (payload: NewMessagePayload) => {
        useMessagingStore.getState()._handleNewMessage(payload);
      },
    );

    // Register listener for message read
    registerCustomEvent(
      MESSAGE_EVENTS.MESSAGE_READ,
      (payload: MessageReadPayload) => {
        useMessagingStore.getState()._handleMessageRead(payload);
      },
    );

    // Register listener for message deleted
    registerCustomEvent(
      MESSAGE_EVENTS.MESSAGE_DELETED,
      (payload: MessageDeletedPayload) => {
        useMessagingStore.getState()._handleMessageDeleted(payload);
      },
    );

    // Register listener for message edited
    registerCustomEvent(
      MESSAGE_EVENTS.MESSAGE_EDITED,
      (payload: MessageEditedPayload) => {
        useMessagingStore.getState()._handleMessageEdited(payload);
      },
    );

    // Register listener for typing start
    registerCustomEvent(
      MESSAGE_EVENTS.TYPING_START,
      (payload: TypingPayload) => {
        useMessagingStore.getState()._handleTypingStart(payload);
      },
    );

    // Register listener for typing stop
    registerCustomEvent(
      MESSAGE_EVENTS.TYPING_STOP,
      (payload: TypingPayload) => {
        useMessagingStore.getState()._handleTypingStop(payload);
      },
    );

    // Register listener for group created
    registerCustomEvent(
      MESSAGE_EVENTS.GROUP_CREATED,
      (payload: GroupCreatedPayload) => {
        useMessagingStore.getState()._handleGroupCreated(payload);
      },
    );

    // Register listener for group updated
    registerCustomEvent(
      MESSAGE_EVENTS.GROUP_UPDATED,
      (payload: GroupUpdatedPayload) => {
        useMessagingStore.getState()._handleGroupUpdated(payload);
      },
    );

    // Register listener for group member added
    registerCustomEvent(
      MESSAGE_EVENTS.GROUP_MEMBER_ADDED,
      (payload: GroupMemberAddedPayload) => {
        useMessagingStore.getState()._handleGroupMemberAdded(payload);
      },
    );

    // Register listener for group member removed
    registerCustomEvent(
      MESSAGE_EVENTS.GROUP_MEMBER_REMOVED,
      (payload: GroupMemberRemovedPayload) => {
        useMessagingStore.getState()._handleGroupMemberRemoved(payload);
      },
    );

    // Register listener for group member left
    registerCustomEvent(
      MESSAGE_EVENTS.GROUP_MEMBER_LEFT,
      (payload: GroupMemberLeftPayload) => {
        useMessagingStore.getState()._handleGroupMemberLeft(payload);
      },
    );

    wsListenersRegistered = true;
    useMessagingStore.setState({
      isSettingUpWebSocketListeners: false,
      websocketListenerError: null,
    });
  } catch (error) {
    registeredMessagingCustomUnsubscribers.forEach((unsubscribe) =>
      unsubscribe(),
    );
    registeredMessagingCustomUnsubscribers = [];
    wsListenersRegistered = false;

    useMessagingStore.setState({
      isSettingUpWebSocketListeners: false,
      websocketListenerError:
        error instanceof Error
          ? error.message
          : "Failed to set up messaging websocket listeners",
    });
  }
};

export const cleanupMessagingWebSocketListeners = (): void => {
  if (!wsListenersRegistered) {
    return;
  }

  registeredMessagingCustomUnsubscribers.forEach((unsubscribe) =>
    unsubscribe(),
  );
  registeredMessagingCustomUnsubscribers = [];

  wsListenersRegistered = false;
};

const cleanupMessagingSocketConnectionListeners = (): void => {
  registeredMessagingSocketUnsubscribers.forEach((unsubscribe) =>
    unsubscribe(),
  );
  registeredMessagingSocketUnsubscribers = [];
};

export const handleMessagingSocketConnected = (): void => {
  setupMessagingWebSocketListeners();

  const now = Date.now();
  if (
    messagingSocketRefreshInFlight ||
    (lastMessagingSocketRefreshAt !== null &&
      now - lastMessagingSocketRefreshAt < MESSAGING_SOCKET_REFRESH_DEBOUNCE_MS)
  ) {
    return;
  }

  lastMessagingSocketRefreshAt = now;
  const state = useMessagingStore.getState();
  const activeConversationId = state.activeConversationId;
  const refreshActiveConversation = activeConversationId
    ? state.loadMessages(activeConversationId, true).then(async () => {
        if (
          useMessagingStore.getState().activeConversationId ===
          activeConversationId
        ) {
          await useMessagingStore.getState().markAsRead(activeConversationId);
        }
      })
    : null;
  const refreshOperations = [
    { name: "fetch_unread_count", promise: state.fetchUnreadCount() },
    { name: "fetch_conversations", promise: state.fetchConversations(true) },
    ...(refreshActiveConversation
      ? [{ name: "load_active_messages", promise: refreshActiveConversation }]
      : []),
  ];
  messagingSocketRefreshInFlight = Promise.allSettled(
    refreshOperations.map((operation) => operation.promise),
  )
    .then((results) => {
      results.forEach((result, index) => {
        if (result.status === "rejected") {
          const operationName = refreshOperations[index]?.name ?? "unknown";
          logBackgroundMessagingError(
            `socket_refresh_${operationName}`,
            result.reason,
          );
        }
      });
    })
    .finally(() => {
      messagingSocketRefreshInFlight = null;
    });
};

const handleMessagingSocketDisconnected = (): void => {
  cleanupMessagingWebSocketListeners();
  resetMessagingSocketRefreshState();
};

// Auto-setup WebSocket listeners when socket connects
if (typeof window !== "undefined") {
  // Try to setup immediately if socket is already connected
  setTimeout(() => {
    if (socketClient.isConnected()) {
      setupMessagingWebSocketListeners();
    }
  }, 1000);

  // Also setup when socket connects later
  cleanupMessagingSocketConnectionListeners();
  registeredMessagingSocketUnsubscribers = [
    socketClient.on("connected", handleMessagingSocketConnected),
    socketClient.on("reconnected", handleMessagingSocketConnected),
    socketClient.on("disconnected", handleMessagingSocketDisconnected),
  ];
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    cleanupMessagingSocketConnectionListeners();
    cleanupMessagingWebSocketListeners();
    resetMessagingSocketRefreshState();
  });
}

// ============================================================================
// Selector Hooks
// ============================================================================

/**
 * Get conversations list
 */
export const useConversations = () =>
  useMessagingStore((state) => state.conversations);

/**
 * Get active conversation
 */
export const useActiveConversation = () =>
  useMessagingStore((state) => {
    if (!state.activeConversationId) return null;
    return (
      state.conversations.find((c) => c.id === state.activeConversationId) ??
      null
    );
  });

/**
 * Get active conversation ID
 */
export const useActiveConversationId = () =>
  useMessagingStore((state) => state.activeConversationId);

/**
 * Get messages for active conversation
 */
export const useMessages = () => useMessagingStore((state) => state.messages);

/**
 * Get pending messages for active conversation
 */
export const usePendingMessages = () =>
  useMessagingStore(
    useShallow((state) =>
      state.activeConversationId
        ? state.pendingMessages.filter(
            (pendingMessage) =>
              pendingMessage.conversationId === state.activeConversationId,
          )
        : [],
    ),
  );

/**
 * Get total unread count
 */
export const useUnreadCount = () =>
  useMessagingStore((state) => state.totalUnreadCount);

/**
 * Get typing users for a specific conversation
 * Note: Returns a stable empty array when no users are typing
 */
const EMPTY_TYPING_USERS: string[] = [];
export const useTypingIndicator = (conversationId: string) =>
  useMessagingStore(
    useShallow((state) => {
      const users = state.typingUsers.get(conversationId);
      return users && users.size > 0 ? Array.from(users) : EMPTY_TYPING_USERS;
    }),
  );

/**
 * Get loading states
 */
export const useMessagingLoading = () =>
  useMessagingStore(
    useShallow((state) => ({
      isLoadingConversations: state.isLoadingConversations,
      isLoadingMessages: state.isLoadingMessages,
      isSendingMessage: state.isSendingMessage,
      isLoadingMore: state.isLoadingMore,
      isSettingUpWebSocketListeners: state.isSettingUpWebSocketListeners,
    })),
  );

/**
 * Get has more messages flag
 */
export const useHasMoreMessages = () =>
  useMessagingStore((state) => state.hasMoreMessages);

/**
 * Get error state
 */
export const useMessagingError = () =>
  useMessagingStore((state) => state.error);

export const useMessagingWebSocketStatus = () =>
  useMessagingStore(
    useShallow((state) => ({
      isSettingUpWebSocketListeners: state.isSettingUpWebSocketListeners,
      websocketListenerError: state.websocketListenerError,
    })),
  );

/**
 * Get composing state (iMessage-style new conversation flow)
 */
export const useComposingState = () =>
  useMessagingStore(
    useShallow((state) => ({
      isComposing: state.isComposing,
      composingRecipients: state.composingRecipients,
    })),
  );

/**
 * Get composing actions
 */
export const useComposingActions = () =>
  useMessagingStore(
    useShallow((state) => ({
      startComposing: state.startComposing,
      cancelComposing: state.cancelComposing,
      addComposingRecipient: state.addComposingRecipient,
      removeComposingRecipient: state.removeComposingRecipient,
      clearComposingRecipients: state.clearComposingRecipients,
      sendComposingMessage: state.sendComposingMessage,
      findExistingConversation: state.findExistingConversation,
      convertToGroup: state.convertToGroup,
    })),
  );

/**
 * Get messaging actions
 */
export const useMessagingActions = () =>
  useMessagingStore(
    useShallow((state) => ({
      fetchConversations: state.fetchConversations,
      fetchConversation: state.fetchConversation,
      startConversation: state.startConversation,
      setActiveConversation: state.setActiveConversation,
      loadMessages: state.loadMessages,
      loadMoreMessages: state.loadMoreMessages,
      sendMessage: state.sendMessage,
      retryPendingMessage: state.retryPendingMessage,
      removePendingMessage: state.removePendingMessage,
      editMessage: state.editMessage,
      deleteMessage: state.deleteMessage,
      markAsRead: state.markAsRead,
      archiveConversation: state.archiveConversation,
      unarchiveConversation: state.unarchiveConversation,
      muteConversation: state.muteConversation,
      unmuteConversation: state.unmuteConversation,
      fetchUnreadCount: state.fetchUnreadCount,
      emitTypingStart: state.emitTypingStart,
      emitTypingStop: state.emitTypingStop,
      clearError: state.clearError,
      // Group actions
      createGroup: state.createGroup,
      updateGroup: state.updateGroup,
      addMembersToGroup: state.addMembersToGroup,
      removeMemberFromGroup: state.removeMemberFromGroup,
      leaveGroup: state.leaveGroup,
      // Composing actions
      startComposing: state.startComposing,
      cancelComposing: state.cancelComposing,
      addComposingRecipient: state.addComposingRecipient,
      removeComposingRecipient: state.removeComposingRecipient,
      clearComposingRecipients: state.clearComposingRecipients,
      sendComposingMessage: state.sendComposingMessage,
      findExistingConversation: state.findExistingConversation,
      convertToGroup: state.convertToGroup,
    })),
  );
